import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { defaultShortcuts, matchesShortcut, isInputElement, SHORTCUT_SCOPES } from '../data/shortcuts';

const ShortcutsContext = createContext();

const STORAGE_KEY = 'paceestate_shortcuts';

export const ShortcutsProvider = ({ children }) => {
  const navigate = useNavigate();
  const [shortcuts, setShortcuts] = useState(defaultShortcuts);
  const [enabled, setEnabled] = useState(true);
  const [vimMode, setVimMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [currentScope, setCurrentScope] = useState(SHORTCUT_SCOPES.GLOBAL);

  // Registry for page-specific shortcuts
  const handlersRef = useRef(new Map());
  const sequenceRef = useRef([]);
  const sequenceTimerRef = useRef(null);

  // Load custom shortcuts from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const customShortcuts = JSON.parse(stored);
        setShortcuts(prev => ({ ...prev, ...customShortcuts }));
      } catch (error) {
        console.error('Failed to load shortcuts:', error);
      }
    }

    // Load vim mode preference
    const vimEnabled = localStorage.getItem('paceestate_vim_mode') === 'true';
    setVimMode(vimEnabled);
  }, []);

  // Save shortcuts to localStorage
  const saveShortcuts = useCallback((newShortcuts) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newShortcuts));
    setShortcuts(newShortcuts);
  }, []);

  // Toggle vim mode
  const toggleVimMode = useCallback((enabled) => {
    setVimMode(enabled);
    localStorage.setItem('paceestate_vim_mode', enabled);

    // Enable/disable vim shortcuts
    setShortcuts(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (updated[key].vim) {
          updated[key].enabled = enabled;
        }
      });
      return updated;
    });
  }, []);

  // Reset shortcuts to defaults
  const resetShortcuts = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setShortcuts(defaultShortcuts);
  }, []);

  // Register a shortcut handler
  const registerHandler = useCallback((action, handler, scope = SHORTCUT_SCOPES.PAGE) => {
    const key = `${scope}:${action}`;
    handlersRef.current.set(key, handler);

    return () => {
      handlersRef.current.delete(key);
    };
  }, []);

  // Unregister a shortcut handler
  const unregisterHandler = useCallback((action, scope = SHORTCUT_SCOPES.PAGE) => {
    const key = `${scope}:${action}`;
    handlersRef.current.delete(key);
  }, []);

  // Execute shortcut action
  const executeAction = useCallback((action, data = {}) => {
    // Check for registered handler
    const globalKey = `${SHORTCUT_SCOPES.GLOBAL}:${action}`;
    const scopeKey = `${currentScope}:${action}`;

    // Try scope-specific handler first, then global
    const handler = handlersRef.current.get(scopeKey) || handlersRef.current.get(globalKey);

    if (handler) {
      handler(data);
      setLastAction({ action, timestamp: Date.now() });
      return true;
    }

    // Handle built-in actions
    switch (action) {
      case 'navigate':
        if (data.path) {
          navigate(data.path);
          setLastAction({ action: 'navigate', path: data.path, timestamp: Date.now() });
          return true;
        }
        break;

      case 'showHelp':
        setShowDialog(true);
        setLastAction({ action: 'showHelp', timestamp: Date.now() });
        return true;

      case 'toggleHelp':
        setShowDialog(prev => !prev);
        setLastAction({ action: 'toggleHelp', timestamp: Date.now() });
        return true;

      case 'commandPalette':
        setShowCommandPalette(prev => !prev);
        setLastAction({ action: 'commandPalette', timestamp: Date.now() });
        return true;

      default:
        return false;
    }
  }, [navigate, currentScope]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    const target = event.target;
    const isInput = isInputElement(target);

    // Build the shortcut string
    const key = event.key.toLowerCase();
    let shortcutStr = '';

    if (event.ctrlKey) shortcutStr += 'ctrl+';
    if (event.metaKey) shortcutStr += 'cmd+';
    if (event.shiftKey && key !== 'shift') shortcutStr += 'shift+';
    if (event.altKey && key !== 'alt') shortcutStr += 'alt+';
    shortcutStr += key;

    // Check for sequence shortcuts (like "g d")
    if (!event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
      // Add to sequence
      sequenceRef.current.push(key);

      // Clear sequence after 1 second
      if (sequenceTimerRef.current) {
        clearTimeout(sequenceTimerRef.current);
      }
      sequenceTimerRef.current = setTimeout(() => {
        sequenceRef.current = [];
      }, 1000);

      // Check for sequence matches
      const sequence = sequenceRef.current.join(' ');
      const sequenceShortcut = shortcuts[sequence];

      if (sequenceShortcut && sequenceShortcut.enabled) {
        if (sequenceShortcut.excludeInputs && isInput) {
          return;
        }

        event.preventDefault();
        sequenceRef.current = [];

        if (sequenceShortcut.action === 'navigate') {
          executeAction('navigate', { path: sequenceShortcut.path });
        } else {
          executeAction(sequenceShortcut.action, sequenceShortcut);
        }
        return;
      }
    }

    // Check for direct shortcut match
    const shortcut = shortcuts[shortcutStr];

    if (!shortcut || !shortcut.enabled) return;

    // Check scope
    if (shortcut.scope === SHORTCUT_SCOPES.PAGE && currentScope !== SHORTCUT_SCOPES.PAGE) {
      return;
    }
    if (shortcut.scope === SHORTCUT_SCOPES.MODAL && currentScope !== SHORTCUT_SCOPES.MODAL) {
      return;
    }
    if (shortcut.scope === SHORTCUT_SCOPES.TABLE && currentScope !== SHORTCUT_SCOPES.TABLE) {
      return;
    }

    // Exclude inputs if specified
    if (shortcut.excludeInputs && isInput) {
      return;
    }

    // Prevent default if specified
    if (shortcut.preventDefault) {
      event.preventDefault();
    }

    // Execute action
    if (shortcut.action === 'navigate') {
      executeAction('navigate', { path: shortcut.path });
    } else {
      executeAction(shortcut.action, shortcut);
    }
  }, [enabled, shortcuts, currentScope, executeAction]);

  // Set up global keyboard listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Check for conflicting shortcuts
  const checkConflicts = useCallback((newShortcut, key) => {
    const conflicts = [];

    Object.entries(shortcuts).forEach(([existingKey, existingShortcut]) => {
      if (existingKey === key) return;

      if (
        existingKey === newShortcut &&
        existingShortcut.scope === shortcuts[key]?.scope
      ) {
        conflicts.push({
          key: existingKey,
          shortcut: existingShortcut,
        });
      }
    });

    return conflicts;
  }, [shortcuts]);

  // Update a shortcut
  const updateShortcut = useCallback((key, updates) => {
    setShortcuts(prev => {
      const updated = {
        ...prev,
        [key]: {
          ...prev[key],
          ...updates,
        },
      };
      saveShortcuts(updated);
      return updated;
    });
  }, [saveShortcuts]);

  // Add a custom shortcut
  const addShortcut = useCallback((key, shortcutConfig) => {
    setShortcuts(prev => {
      const updated = {
        ...prev,
        [key]: shortcutConfig,
      };
      saveShortcuts(updated);
      return updated;
    });
  }, [saveShortcuts]);

  // Remove a shortcut
  const removeShortcut = useCallback((key) => {
    setShortcuts(prev => {
      const updated = { ...prev };
      delete updated[key];
      saveShortcuts(updated);
      return updated;
    });
  }, [saveShortcuts]);

  const value = {
    shortcuts,
    enabled,
    vimMode,
    showDialog,
    showCommandPalette,
    lastAction,
    currentScope,
    setEnabled,
    setShowDialog,
    setShowCommandPalette,
    setCurrentScope,
    toggleVimMode,
    resetShortcuts,
    registerHandler,
    unregisterHandler,
    executeAction,
    checkConflicts,
    updateShortcut,
    addShortcut,
    removeShortcut,
    saveShortcuts,
  };

  return (
    <ShortcutsContext.Provider value={value}>
      {children}
    </ShortcutsContext.Provider>
  );
};

export const useShortcutsContext = () => {
  const context = useContext(ShortcutsContext);
  if (!context) {
    throw new Error('useShortcutsContext must be used within ShortcutsProvider');
  }
  return context;
};

export default ShortcutsContext;
